import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';
import { userInfoStore } from '../../../store/userInfoStore';
import { mathConfig, mathJS } from '../../../core/helpers/mathFormat';
import {
  parseNumberByPrecision,
  parseNumberPrecisionByDoteCount,
} from '../../../core/helpers/parseNumberByPrecision';
import { ADDITIONAL_PARAMS_TYPE } from '../handBlackjackCore/constants';
import { soundService } from '../../../services/baseSoundService/soundService';
import { notificationStore } from '../../../store/notificationStore';
import { ValidationType } from '../../../core/constants/validationType';
import { autoBetStore } from '../../../store/autoBetStore';
import { HandBlackJackSoundsName } from '../handBlackjackCore/constants/sounds';
import { betHistoryDetailsService } from '../../blackjack/blackJackService/betHistoryDetailsService';
import { checkHashStore } from '../../../store/checkHashStore';
import { freeBetStore } from '../../../store/freeBetStore';

class HandBlackJackBetStore {
  betsModels = [];

  isAllowSendDecision = false;

  reBet = null;

  isDoSpinStart = false;

  isDisableDouble = false;

  isSplitCardAnimationByBox = 0;

  isInsurance = null;

  isShowDecision = false;

  activeBox = null;

  activeHandByBox = null;

  activeAnimationBox = null;

  activeBetId = null;

  betHistoryDetails = null;

  winAmount = null;

  handsOdds = null;

  winOdd = null;

  possibleActions = [];

  isSelectedDecision = false;

  isInsuranceDecisionOpened = false;

  dealerInsuranceAnimationStart = false;

  constructor() {
    makeObservable(this, {
      lastBetModel: computed,
      betsTotalAmount: computed,
      activeBox: observable,
      isDoSpinStart: observable,
      activeBetId: observable,
      isDisableDouble: observable,
      reBet: observable,
      isInsurance: observable,
      winAmount: observable,
      winOdd: observable,
      isShowDecision: observable,
      possibleActions: observable,
      handsOdds: observable,
      activeHandByBox: observable,
      activeAnimationBox: observable,
      isSelectedDecision: observable,
      isInsuranceDecisionOpened: observable,
      isSplitCardAnimationByBox: observable,
      dealerInsuranceAnimationStart: observable,
      isAllowSendDecision: observable,
      betsModels: observable.deep,
      betHistoryDetails: observable.deep,
      setDouble: action.bound,
      setUndoBet: action.bound,
      setWinAmount: action.bound,
      setWinOdd: action.bound,
      setBetsModel: action.bound,
      setHandsOdds: action.bound,
      setIsDisableDouble: action.bound,
      setIsAllowSendDecision: action.bound,
      setIsDoSpinStart: action.bound,
      updateBetsModel: action.bound,
      updateInsuranceAmount: action.bound,
      handleInitialBetsModel: action.bound,
      doubleBetAmountByAction: action.bound,
      updateSurrenderAmountByBox: action.bound,
      setActiveBox: action.bound,
      setActiveBetId: action.bound,
      setBetHistoryDetails: action.bound,
      setIsInsurance: action.bound,
      setActiveHandByBox: action.bound,
      setPossibleActions: action.bound,
      updateIsShowDecision: action.bound,
      removeBetModel: action.bound,
      setActiveAnimationBox: action.bound,
      setIsSplitCardAnimationByBox: action.bound,
      setReBet: action.bound,
      setIsSelectedDecision: action.bound,
      setDealerInsuranceAnimationStart: action.bound,
      setIsInsuranceDecisionOpened: action.bound,
      getBetHistoryDetails: flow.bound,
    });
    reaction(
      () => this.isDoSpinStart,
      (value) => {
        // const { setIsAllowedChangeCurrency } = userInfoStore;
        // setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => freeBetStore.currentFreeBet,
      () => {
        const { setSelectedChip, defaultBetAmount } = userInfoStore;
        const selectedQuickBet = userInfoStore.quickBets.find(
          (item) => freeBetStore.currentFreeBet && item.value === freeBetStore.currentFreeBet.amount
        );
        const defaultChip = userInfoStore.quickBets.find((item) => item.value === defaultBetAmount);

        if (defaultChip || selectedQuickBet) {
          const betToUse = selectedQuickBet || defaultChip;
          if (betToUse?.chip) {
            setSelectedChip(betToUse.chip);
          }
        }
      }
    );
    reaction(
      () => [this.lastBetModel, this.betsTotalAmount, userInfoStore.limits],
      () => {
        this.setIsDisableDouble(
          this.lastBetModel?.betModel &&
            userInfoStore.limits &&
            this.betsTotalAmount >= this.lastBetModel.betModel.length * userInfoStore.limits.maxBet
        );
      }
    );
  }

  setDealerInsuranceAnimationStart(bool) {
    this.dealerInsuranceAnimationStart = bool;
  }

  setIsSplitCardAnimationByBox(bool) {
    this.isSplitCardAnimationByBox = bool;
  }

  setIsInsurance(bool) {
    this.isInsurance = bool;
  }

  setIsDisableDouble(bool) {
    this.isDisableDouble = bool;
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  setWinOdd(winOdd) {
    this.winOdd = winOdd;
  }

  setHandsOdds(spins) {
    if (spins) {
      this.handsOdds = spins.reduce((acc, cur) => {
        if (
          cur.type !== ADDITIONAL_PARAMS_TYPE.INSURANCE_BET &&
          cur.type !== ADDITIONAL_PARAMS_TYPE.DOUBLE
        ) {
          acc[`${cur.boxIndex}_${cur.handType}`] = {
            odd: cur.odd,
            status: cur.status,
          };
        }
        return acc;
      }, {});
    }
  }

  setIsSelectedDecision(bool) {
    this.isSelectedDecision = bool;
  }

  setActiveBetId(id) {
    this.activeBetId = id;
  }

  updateIsShowDecision(bool) {
    this.isShowDecision = bool;
  }

  setActiveBox(boxIndex) {
    this.activeBox = boxIndex;
  }

  setActiveHandByBox(activeHandByBox) {
    this.activeHandByBox = activeHandByBox;
  }

  setActiveAnimationBox(activeAnimationBox) {
    this.activeAnimationBox = activeAnimationBox;
  }

  setIsInsuranceDecisionOpened(bool) {
    this.isInsuranceDecisionOpened = bool;
  }

  setPossibleActions(possibleActions) {
    this.possibleActions = possibleActions;
  }

  updateSurrenderAmountByBox(boxIndex) {
    const { betModel } = this.lastBetModel;
    const newBetModel = betModel.map((item) => {
      return item.boxIndex === boxIndex
        ? {
            boxIndex,
            chip: parseNumberByPrecision(
              +mathJS.format(item.chip / 2, mathConfig),
              1,
              +userInfoStore.precision
            ),
            amount: parseNumberByPrecision(
              +mathJS.format(item.amount / 2, mathConfig),
              1,
              +userInfoStore.precision
            ),
          }
        : item;
    });
    this.betsModels = [{ betModel: newBetModel }];
  }

  handleInitialBetsModel(spins, minQuickBet) {
    const onlyMainSpins = spins.filter((spin) => spin.mainSpin);
    this.setBetsModel([
      {
        betModel: onlyMainSpins.map((item) => {
          const { winAmount, ...all } = item.mainSpin;
          const isDoubleOrSplitSpin = spins.find(
            (spin) => (spin.splitSpin || spin.mainDoubleSpin) && spin.boxIndex === item.boxIndex
          );
          const chipAmount = isDoubleOrSplitSpin
            ? parseNumberByPrecision(
                1,
                +mathJS.format(all.amount * 2, mathConfig),
                +userInfoStore.precision
              )
            : all.amount;
          const chip = parseNumberByPrecision(
            1,
            Math.round(+mathJS.format(chipAmount / minQuickBet, mathConfig)),
            +userInfoStore.precision
          );
          return {
            ...all,
            boxIndex: item.boxIndex,
            amount: chipAmount,
            chip,
          };
        }),
      },
    ]);
  }

  setBetHistoryDetails(betHistoryDetails) {
    this.betHistoryDetails = betHistoryDetails;
  }

  *getBetHistoryDetails(mainSpinId) {
    const {
      data: { result },
    } = yield betHistoryDetailsService.getBetHistory({ mainSpinId });
    checkHashStore.setSpinInfo({
      salt: result.salt,
      key: result.key,
      hash: result.hash,
    });
    this.setBetHistoryDetails(result);
  }

  setIsAllowSendDecision(bool) {
    this.isAllowSendDecision = bool;
  }

  setUndoBet() {
    if (this.betsModels.length) {
      soundService.play(HandBlackJackSoundsName.chip);
      this.betsModels.pop();
    }
  }

  removeBetModel() {
    if (this.betsModels.length) {
      soundService.play(HandBlackJackSoundsName.chip);
      this.betsModels = [];
    }
  }

  setReBet(betData) {
    this.reBet = betData;
  }

  setBetsModel(betsModel) {
    this.betsModels = betsModel;
  }

  setDouble() {
    const { userBalance, limits, quickBets } = userInfoStore;
    if (this.betsModels.length && !this.isDisableDouble) {
      const nextTotalAmount = this.betsTotalAmount * 2;
      const isDoubleValidInBalance =
        nextTotalAmount <= userBalance && nextTotalAmount <= limits.maxBet;
      if (isDoubleValidInBalance) {
        soundService.play(HandBlackJackSoundsName.chip);
        const doubleBetModel = this.betsModels[this.betsModels.length - 1].betModel.map((item) => {
          const doubleAmount = parseNumberPrecisionByDoteCount(
            item.amount,
            2,
            userInfoStore.precision
          );
          const doubleChip = parseNumberPrecisionByDoteCount(item.chip, 2, userInfoStore.precision);
          const finalAmount = doubleAmount > limits.maxBet ? limits.maxBet : doubleAmount;
          const finalChip =
            doubleChip > quickBets[quickBets.length - 1].chip
              ? quickBets[quickBets.length - 1].chip
              : doubleChip;
          return {
            ...item,
            amount: finalAmount,
            chip: finalChip,
          };
        });
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: doubleBetModel,
          },
        ];
      } else {
        notificationStore.setNotification(ValidationType.NotEnoughMoney);
      }
    }
  }

  get lastBetModel() {
    return this.betsModels[this.betsModels.length - 1] || {};
  }

  get betsTotalAmount() {
    if (this.lastBetModel.betModel) {
      return this.lastBetModel.betModel.reduce((acc, curr) => {
        acc += curr.amount;
        return parseNumberByPrecision(1, acc, +userInfoStore.precisionForBetModel);
      }, 0);
    }
    return 0;
  }

  updateBetsModel(data) {
    if (autoBetStore.isAutoBetStart || this.isDoSpinStart) {
      return;
    }
    const { currentFreeBet } = freeBetStore;
    const { userBalance, betAmount, displayCurrency, exchangedBetAmount, limits } = userInfoStore;
    const totalBets = displayCurrency ? exchangedBetAmount : this.betsTotalAmount;
    const betTypeTotalAmount = this.lastBetModel?.betModel?.find(
      (item) => item.boxIndex === data.boxIndex
    );
    if (!currentFreeBet && totalBets + betAmount > userBalance) {
      notificationStore.setNotification(ValidationType.NotEnoughMoney);
      return;
    }
    if (!currentFreeBet && betAmount + (betTypeTotalAmount?.amount || 0) > limits.maxBet) {
      notificationStore.setNotification(ValidationType.MaxBetLimit);
      return;
    }

    soundService.play(HandBlackJackSoundsName.chip);
    if (!this.betsModels.length) {
      this.betsModels.push({
        betModel: [data],
      });
    } else {
      const otherBetModel = this.lastBetModel.betModel.filter(
        (item) => item.boxIndex !== data.boxIndex
      );
      if (betTypeTotalAmount) {
        const amount = parseNumberByPrecision(
          1,
          +mathJS.format(betTypeTotalAmount.amount + data.amount, mathConfig),
          +userInfoStore.precisionForBetModel
        );
        const chip = parseNumberByPrecision(
          1,
          +mathJS.format(betTypeTotalAmount.chip + data.chip, mathConfig),
          +userInfoStore.precisionForBetModel
        );
        !currentFreeBet &&
          (this.betsModels = [
            ...this.betsModels,
            {
              betModel: [...otherBetModel, { ...betTypeTotalAmount, amount, chip }],
            },
          ]);
      } else if (currentFreeBet) {
        if (this.betsModels.length >= currentFreeBet.remainingCount) {
          // eslint-disable-next-line consistent-return
          return null;
        }
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...data }],
          },
        ];
      } else {
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...data }],
          },
        ];
      }
    }
  }

  doubleBetAmountByAction(boxIndex) {
    const { betModel } = this.lastBetModel;
    const newBetModel = betModel.map((item) => {
      return item.boxIndex === boxIndex
        ? {
            boxIndex,
            chip: parseNumberByPrecision(
              +mathJS.format(item.chip * 2, mathConfig),
              1,
              +userInfoStore.precision
            ),
            amount: parseNumberByPrecision(
              +mathJS.format(item.amount * 2, mathConfig),
              1,
              +userInfoStore.precision
            ),
          }
        : item;
    });
    this.betsModels = [{ betModel: newBetModel }];
  }

  updateInsuranceAmount(isLose) {
    const { betModel } = this.lastBetModel;
    const newBetModel = betModel.map((item) => {
      if (isLose) {
        return {
          boxIndex: item.boxIndex,
          chip: parseNumberByPrecision(
            +mathJS.format(item.chip - +mathJS.format(item.chip / 3, mathConfig), mathConfig),
            1,
            +userInfoStore.precision
          ),
          amount: parseNumberByPrecision(
            +mathJS.format(item.amount - +mathJS.format(item.amount / 3, mathConfig), mathConfig),
            1,
            +userInfoStore.precision
          ),
        };
      }
      return {
        boxIndex: item.boxIndex,
        chip: parseNumberByPrecision(
          +mathJS.format(item.chip + +mathJS.format(item.chip / 2, mathConfig), mathConfig),
          1,
          +userInfoStore.precision
        ),
        amount: parseNumberByPrecision(
          +mathJS.format(item.amount + +mathJS.format(item.amount / 2, mathConfig), mathConfig),
          1,
          +userInfoStore.precision
        ),
      };
    });
    this.betsModels = [{ betModel: newBetModel }];
  }

  setIsDoSpinStart(bool) {
    this.isDoSpinStart = bool;
  }

  handleToInitStore() {
    this.isDoSpinStart = false;
    this.betsModels = [];
    this.handsOdds = null;
    this.isSplitCardAnimationByBox = 0;
    this.isInsurance = null;
    this.winAmount = null;
    this.winOdd = null;
    this.isShowDecision = false;
    this.activeBox = null;
    this.activeHandByBox = null;
    this.activeAnimationBox = null;
    this.activeBetId = null;
    this.possibleActions = [];
    this.isSelectedDecision = false;
    this.isInsuranceDecisionOpened = false;
    this.dealerInsuranceAnimationStart = false;
  }
}

export const handBlackJackBetStore = new HandBlackJackBetStore();
